/**
 * Offline Map Component
 *
 * Last-resort placeholder when the full Mapbox/OSM cascade has failed.
 * Renders a static gray surface with the current centre coordinates —
 * NOT a real interactive map. We deliberately do NOT render the parent's
 * children here: callers pass `react-map-gl` <Marker> / <Popup> elements
 * that crash without the Mapbox `MapContext`. Showing them in this plain
 * <div> blew up the whole page in production. If the cascade reaches this
 * state, the user gets a clear "map unavailable" message instead of a
 * blank screen + console exceptions.
 */

import React from 'react';

export interface OfflineMapProps {
  viewState: {
    latitude: number;
    longitude: number;
    zoom: number;
  };
  onMoveStart: () => void;
  onMove: (e: any) => void;
  onMoveEnd: (e: any) => void;
  /**
   * Intentionally accepted but NOT rendered — see component docblock for
   * why. Kept in the prop type so MapView's call site doesn't need a
   * conditional and so future authors can't accidentally pass them
   * expecting them to work.
   */
  children?: React.ReactNode;
}

export const OfflineMap: React.FC<OfflineMapProps> = ({
  viewState,
  onMoveStart: _onMoveStart,
  onMove: _onMove,
  onMoveEnd: _onMoveEnd,
  children: _children
}) => {
  return (
    <div className="w-full h-full bg-gray-200 relative overflow-hidden rounded-lg">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-100 to-green-100 opacity-50" />

      <div
        className="absolute inset-0 opacity-20"
        style={{
          backgroundImage: `
            linear-gradient(to right, #ccc 1px, transparent 1px),
            linear-gradient(to bottom, #ccc 1px, transparent 1px)
          `,
          backgroundSize: '20px 20px'
        }}
      />

      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-2 text-center px-4">
        <div className="w-4 h-4 bg-red-500 rounded-full border-2 border-white shadow-lg" />
        <div className="rounded-lg bg-white/95 px-4 py-2 shadow-md max-w-xs">
          <div className="text-sm font-semibold text-gray-800">
            تعذّر تحميل الخريطة
          </div>
          <div className="mt-1 text-xs text-gray-600">
            تحقّق من اتصال الإنترنت أو إعدادات Mapbox، ثم أعد تحميل الصفحة.
          </div>
        </div>
      </div>

      <div className="absolute bottom-4 left-4 bg-white bg-opacity-90 px-3 py-2 rounded-lg shadow-md text-sm">
        <div className="font-mono text-gray-700">
          Lat: {viewState.latitude.toFixed(4)}
        </div>
        <div className="font-mono text-gray-700">
          Lng: {viewState.longitude.toFixed(4)}
        </div>
        <div className="font-mono text-gray-700">
          Zoom: {viewState.zoom.toFixed(1)}
        </div>
      </div>

      <div className="absolute top-4 left-4 bg-yellow-100 border border-yellow-300 px-3 py-2 rounded-lg shadow-md">
        <div className="flex items-center gap-2 text-sm text-yellow-800">
          <div className="w-2 h-2 bg-yellow-500 rounded-full" />
          Offline Mode
        </div>
      </div>
    </div>
  );
};

export default OfflineMap;
